import { createTheme } from "@mui/material/styles";

// Colori estratti dal sito Eli Partner Program
const eliPartnerProgramColors = {
  primary: {
    main: "#434a54",
    light: "#303030",
    dark: "#2d313a",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#434a54",
    light: "#303030",
    dark: "#2d313a",
    contrastText: "#ffffff",
  },
  background: {
    default: "#f1f1f1",
    paper: "#ffffff",
    navbar: "#e9e3d9",
  },
  text: {
    primary: "#303030",
    secondary: "#999999",
    disabled: "#cccccc",
  },
  divider: "#efefef",
  success: {
    main: "#333333",
  },
  warning: {
    main: "#ff9800",
  },
  error: {
    main: "#f44336",
  },
  info: {
    main: "#48cfad",
  },
};

// Tema Material-UI personalizzato per Eli Partner Program
export const eliPartnerProgramTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: eliPartnerProgramColors.primary.main,
      light: eliPartnerProgramColors.primary.light,
      dark: eliPartnerProgramColors.primary.dark,
      contrastText: eliPartnerProgramColors.primary.contrastText,
    },
    secondary: {
      main: eliPartnerProgramColors.secondary.main,
      light: eliPartnerProgramColors.secondary.light,
      dark: eliPartnerProgramColors.secondary.dark,
      contrastText: eliPartnerProgramColors.secondary.contrastText,
    },
    background: {
      default: eliPartnerProgramColors.background.default,
      paper: eliPartnerProgramColors.background.paper,
    },
    text: {
      primary: eliPartnerProgramColors.text.primary,
      secondary: eliPartnerProgramColors.text.secondary,
      disabled: eliPartnerProgramColors.text.disabled,
    },
    divider: eliPartnerProgramColors.divider,
    success: {
      main: eliPartnerProgramColors.success.main,
    },
    warning: {
      main: eliPartnerProgramColors.warning.main,
    },
    error: {
      main: eliPartnerProgramColors.error.main,
    },
    info: {
      main: eliPartnerProgramColors.info.main,
    },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 800,
      fontSize: "2.5rem",
      lineHeight: 1.2,
      color: eliPartnerProgramColors.text.primary,
      textTransform: "uppercase",
    },
    h2: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
      fontSize: "2rem",
      lineHeight: 1.3,
      color: eliPartnerProgramColors.text.primary,
      textTransform: "uppercase",
    },
    h3: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
      fontSize: "1.75rem",
      lineHeight: 1.4,
      color: eliPartnerProgramColors.text.primary,
    },
    h4: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
      fontSize: "1.5rem",
      lineHeight: 1.4,
      color: eliPartnerProgramColors.text.primary,
    },
    h5: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.5,
      color: eliPartnerProgramColors.text.primary,
    },
    h6: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.5,
      color: eliPartnerProgramColors.text.primary,
    },
    subtitle1: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      fontSize: "1rem",
      lineHeight: 1.75,
      color: eliPartnerProgramColors.text.primary,
    },
    subtitle2: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 500,
      fontSize: "0.875rem",
      lineHeight: 1.57,
      color: eliPartnerProgramColors.text.secondary,
    },
    body1: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 400,
      fontSize: "1rem",
      lineHeight: 1.5,
      color: eliPartnerProgramColors.text.primary,
    },
    body2: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 400,
      fontSize: "0.875rem",
      lineHeight: 1.43,
      color: eliPartnerProgramColors.text.primary,
    },
    button: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 700,
      fontSize: "0.875rem",
      lineHeight: 1.75,
      textTransform: "uppercase",
    },
    caption: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 400,
      fontSize: "0.75rem",
      lineHeight: 1.66,
      color: eliPartnerProgramColors.text.secondary,
    },
    overline: {
      fontFamily: "'Inter', sans-serif",
      fontWeight: 600,
      fontSize: "0.75rem",
      lineHeight: 2.66,
      textTransform: "uppercase",
      color: eliPartnerProgramColors.text.secondary,
    },
  },
  shape: {
    borderRadius: 3, // Simile al design del sito originale
  },
  components: {
    // Personalizzazione dei componenti Material-UI
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          fontWeight: 700,
          textTransform: "uppercase",
          transition: "all 0.3s ease-in-out",
        },
        contained: {
          backgroundColor: eliPartnerProgramColors.primary.main,
          color: "#ffffff",
          "&:hover": {
            backgroundColor: eliPartnerProgramColors.primary.dark,
            transform: "translateY(-1px)",
            boxShadow: "0 4px 8px rgba(55, 188, 155, 0.3)",
          },
        },
        outlined: {
          borderColor: eliPartnerProgramColors.primary.main,
          color: eliPartnerProgramColors.primary.main,
          "&:hover": {
            backgroundColor: eliPartnerProgramColors.primary.main,
            color: "#ffffff",
            borderColor: eliPartnerProgramColors.primary.main,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: eliPartnerProgramColors.background.navbar,
          color: eliPartnerProgramColors.text.primary,
          boxShadow: "0 1px 3px rgba(0,0,0,.25)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          boxShadow: "0 0 1px 1px #ddd",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 3,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputLabel-root": {
            fontWeight: 450,
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: 7,
            "&:hover fieldset": {
              borderColor: eliPartnerProgramColors.primary.light,
            },
            "&.Mui-focused fieldset": {
              borderColor: eliPartnerProgramColors.primary.main,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          fontFamily: "'Inter', sans-serif",
          fontWeight: 600,
        },
        filled: {
          backgroundColor: eliPartnerProgramColors.primary.main,
          color: "#ffffff",
          "&:hover": {
            backgroundColor: eliPartnerProgramColors.primary.dark,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: eliPartnerProgramColors.primary.main,
          height: 3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif",
          fontWeight: 700,
          textTransform: "uppercase",
          color: eliPartnerProgramColors.text.primary,
          "&.Mui-selected": {
            color: eliPartnerProgramColors.primary.main,
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

// Colori aggiuntivi per uso specifico (se necessario)
export const additionalColors = {
  navbar: eliPartnerProgramColors.background.navbar,
  accent: eliPartnerProgramColors.primary.light,
  muted: eliPartnerProgramColors.text.secondary,
  border: eliPartnerProgramColors.divider,
} as const;

export default eliPartnerProgramTheme;
